import React, { useRef, useState, useEffect } from 'react';
import { useReactive } from 'ahooks';
import { Input, Button, Upload } from 'kenshin';
import {
  SendOutlined,
  PaperClipOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  imageModels,
  imageQuantityOptions,
  videoModels,
  sizeRatioToWidthHeight,
} from '../../config/models';
import './index.less';

const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

// AI会话模型选项（文档暂无对应，保留原有）
const chatModelOptions = [
  { label: 'OpenAI GPT', value: 'openai_gpt' },
  {
    label: '阿里通义', value: 'ali_tongyi', children: [
      { label: '万相2.6', value: 'wanxiang_2.6' },
      { label: '万相2.7', value: 'wanxiang_2.7' },
      { label: '通义千问', value: 'tongyi_qianwen' },
    ]
  },
  { label: '腾讯混元', value: 'tencent_hunyuan' },
  { label: '字节豆包', value: 'bytedance_doubao' },
  { label: '豆包', value: 'doubao' },
];

// AI视频时长选项
const videoDurationOptions = [
  { label: '4s', value: '4' },
  { label: '5s', value: '5' },
  { label: '6s', value: '6' },
  { label: '7s', value: '7' },
  { label: '8s', value: '8' },
  { label: '9s', value: '9' },
  { label: '10s', value: '10' },
  { label: '11s', value: '11' },
  { label: '12s', value: '12' },
  { label: '13s', value: '13' },
  { label: '14s', value: '14' },
  { label: '15s', value: '15' },
];

// 下拉选择组件
const DropdownSelect = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="dropdownWrapper" ref={ref}>
      <div
        className="dropdownTrigger"
        onClick={() => setOpen(!open)}
      >
        {selected?.label || placeholder || options[0]?.label}
      </div>
      {open && (
        <div className="dropdownMenu">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`dropdownItem ${opt.value === value ? 'dropdownItemActive' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="dropdownItemLabel">{opt.label}</span>
              {opt.value === value && <CheckOutlined className="dropdownItemCheck" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InputArea = (props) => {
  const { mode, onSend, onModeChange } = props;
  const textareaRef = useRef();

  const modeOptions = [
    { label: 'AI生图', value: MODES.IMAGE },
    { label: 'AI视频', value: MODES.VIDEO },
  ];

  // 默认模型取 config/models.js 第一个
  const defaultImageModel = imageModels[0]?.value || '';
  const defaultVideoModel = videoModels[0]?.value || '';

  const state = useReactive({
    text: '',
    images: [],
    // 生图参数
    imageModel: defaultImageModel,
    imageSize: '2K',
    imageRatio: '1:1',
    imageQuantity: imageQuantityOptions[0]?.value || 1,
    // 视频参数
    videoModel: defaultVideoModel,
    videoRatio: '16:9',
    videoResolution: '720p',
    videoDuration: '5',
    // 会话参数
    chatModel: 'wanxiang_2.6',
  });

  // 发送消息
  const handleSend = () => {
    if (!state.text.trim() && state.images.length === 0) return;

    let params;
    if (mode === MODES.IMAGE) {
      const { width, height } = sizeRatioToWidthHeight(state.imageSize, state.imageRatio);
      params = {
        modelCode: state.imageModel,
        size: state.imageSize,
        width,
        height,
        quantity: state.imageQuantity,
      };
    } else if (mode === MODES.VIDEO) {
      params = {
        modelCode: state.videoModel,
        ratio: state.videoRatio,
        resolution: state.videoResolution,
        duration: state.videoDuration,
      };
    } else {
      params = {
        model: state.chatModel,
      };
    }

    onSend({
      text: state.text,
      images: [...state.images],
      mode,
      params,
    });
    state.text = '';
    state.images = [];
  };

  // 上传图片
  const handleUpload = (file) => {
    const url = URL.createObjectURL(file);
    state.images = [...state.images, url];
    return false; // 阻止默认上传行为
  };

  // 移除图片
  const removeImage = (index) => {
    state.images = state.images.filter((_, i) => i !== index);
  };

  // 键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 渲染AI会话输入区
  const renderChatInput = () => (
    <div className="paramRow">
      {renderUpload()}
      <DropdownSelect
        options={chatModelOptions}
        value={state.chatModel}
        onChange={(v) => (state.chatModel = v)}
      />
    </div>
  );

  const renderUpload = () => (
    <div className="footerLeft">
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={handleUpload}
      >
        <PaperClipOutlined className="footerIcon" />
      </Upload>
    </div>
  );

  // 渲染AI生图输入区
  const renderImageInput = () => {
    const currentModel = imageModels.find((m) => m.value === state.imageModel);
    const availableSizes = (currentModel?.sizes || ['1K', '2K', '4K']).map((s) => ({
      label: s,
      value: s,
    }));
    const availableRatios = (currentModel?.ratios || ['16:9', '4:3', '1:1', '3:4', '9:16']).map((r) => ({
      label: r,
      value: r,
    }));

    return (
      <div className="paramRow">
        {renderUpload()}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => onModeChange?.(v)}
        />
        <DropdownSelect
          options={imageModels}
          value={state.imageModel}
          onChange={(v) => (state.imageModel = v)}
        />
        <DropdownSelect
          options={availableSizes}
          value={state.imageSize}
          onChange={(v) => (state.imageSize = v)}
        />
        <DropdownSelect
          options={availableRatios}
          value={state.imageRatio}
          onChange={(v) => (state.imageRatio = v)}
        />
        <DropdownSelect
          options={imageQuantityOptions}
          value={state.imageQuantity}
          onChange={(v) => (state.imageQuantity = v)}
        />
      </div>
    );
  };

  // 渲染AI视频输入区
  const renderVideoInput = () => {
    const currentModel = videoModels.find((m) => m.value === state.videoModel);
    const availableResolutions = (currentModel?.resolutions || ['720p', '1080p']).map((r) => ({
      label: r,
      value: r,
    }));
    const availableRatios = (currentModel?.ratios || ['16:9', '4:3', '1:1', '3:4', '9:16']).map((r) => ({
      label: r,
      value: r,
    }));

    return (
      <div className="paramRow">
        {renderUpload()}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => onModeChange?.(v)}
        />
        <DropdownSelect
          options={videoModels}
          value={state.videoModel}
          onChange={(v) => (state.videoModel = v)}
        />
        <DropdownSelect
          options={availableRatios}
          value={state.videoRatio}
          onChange={(v) => (state.videoRatio = v)}
        />
        <DropdownSelect
          options={availableResolutions}
          value={state.videoResolution}
          onChange={(v) => (state.videoResolution = v)}
        />
        <DropdownSelect
          options={videoDurationOptions}
          value={state.videoDuration}
          onChange={(v) => (state.videoDuration = v)}
        />
      </div>
    );
  };

  const placeholderTexts = {
    [MODES.CHAT]: '请问有什么需要帮助的？',
    [MODES.IMAGE]: '上传参考图，输入文字，描述你想要生成的图片。',
    [MODES.VIDEO]: '上传参考图，输入文字，描述你想要生成的视频。',
  };

  return (
    <div className="inputArea">
      {/* 已上传的图片预览 */}
      {state.images.length > 0 && (
        <div className="uploadedImages">
          {state.images.map((img, idx) => (
            <div key={idx} className="uploadedImgItem">
              <img src={img} alt="" />
              <div className="uploadedImgRemove" onClick={() => removeImage(idx)}>
                <CloseOutlined />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 文本输入 */}
      <Input.TextArea
        ref={textareaRef}
        value={state.text}
        onChange={(e) => (state.text = e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholderTexts[mode]}
        className="textarea"
        autoSize={{ minRows: 2, maxRows: 6 }}
        bordered={false}
      />


      <div className="inputWrapper">
        {/* 参数选择行 */}
        {mode === MODES.CHAT && renderChatInput()}
        {mode === MODES.IMAGE && renderImageInput()}
        {mode === MODES.VIDEO && renderVideoInput()}
        <div className="footerRight">
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={!state.text.trim() && state.images.length === 0}
            className="sendBtn"
          />
        </div>
      </div>
    </div>
  );
};

export default InputArea;
